import {createAsyncThunk, createSlice, PayloadAction} from "@reduxjs/toolkit";
import {api} from "../api";
import {
    DataParams, FormResult,
    GameResult, LinkResult,
    ListType,
    NavigationParams, QuestionResult,
    TeamResult,
    ThemeResult,
    UserResult,
    CollectionResult
} from "../../types/admin";
import {setGame} from "./admin";
import {format} from "date-fns";
import {downloadBlob} from "../../components/admin/utils";

export type AdminListState = {
    loaded: boolean
    error: string | null
    count: number
    next: string | null
    previous: string | null
    listType: ListType | null
    results: GameResult[] | UserResult[] | TeamResult[] | ThemeResult[] | QuestionResult[] | FormResult[] | LinkResult[] | CollectionResult[]
};

const initialState: AdminListState = {
    loaded: false,
    error: null,
    count: 0,
    next: null,
    previous: null,
    listType: null,
    results: [],
};

export type AdminListParams = DataParams & {
    listType: ListType
};

export const extractParams = (params: AdminListParams): NavigationParams => {
    let _params: any = {};
    if (params.page) _params.page = parseInt(params.page);
    if (params.status) _params.status = parseInt(params.status);
    if (params.sq) _params.search = params.sq;
    if (params.language) _params.language = params.language;
    if (params.type) _params.type = params.type;
    if (params.sort_direction) _params.sort_direction = params.sort_direction;
    if (params.sort_value) _params.sort_value = params.sort_value;
    if (params.is_active) _params.is_active = params.is_active;
    if (params.is_staff) _params.is_staff = params.is_staff;
    if (params.is_superuser) _params.is_superuser = params.is_superuser;
    if (params.team) _params.team = params.team;
    if (params.game) _params.game = params.game;
    if (params.company) _params.company = params.company;
    return _params;
}

export const getListItems = createAsyncThunk(
    'adminList/getListItems',
    async (params: AdminListParams, thunkAPI) => {
        const dispatch = thunkAPI.dispatch;
        const _params = extractParams(params);
        try {
            let endpoint = '/API/admin/games/';
            if (params.listType === 'users') {
                endpoint = '/API/admin/users/';
            } else if (params.listType === 'teams') {
                endpoint = '/API/admin/teams/';
            } else if (params.listType === 'themes') {
                endpoint = '/API/admin/themes/';
            } else if (params.listType === 'questions') {
                endpoint = '/API/admin/questions/';
            } else if (params.listType === 'forms') {
                endpoint = '/API/admin/forms/';
            } else if (params.listType === 'links') {
                endpoint = '/API/admin/links/';
            } else if (params.listType === 'collections') {
                endpoint = '/API/admin/collections/';
            }
            const response = await api.get(endpoint, _params);
            if (response.filter_game){
                dispatch(setGame(response.filter_game))
            }
            return {
                ...response,
                listType: params.listType,
            };
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);

export const getFormExcel = createAsyncThunk(
    'adminList/getFormExcel',
    async (form_id:string, thunkAPI) => {
        try {
            return await api.getExcel(`/API/admin/forms/${form_id}/export/` );
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);

export const getInviteLinkExcel = createAsyncThunk(
    'adminList/getInviteLinkExcel',
    async (params: AdminListParams, thunkAPI) => {
        try {
            const _params = extractParams(params);
            return await api.getExcel(`/API/admin/links/export/`, _params);
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);


export type AdminListPayload = {
    result: GameResult | UserResult | TeamResult | ThemeResult | QuestionResult | FormResult | CollectionResult
};




const slice = createSlice({
    name: 'adminList',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        deleteResult: (state, action) => {
            const { key } = action.payload || {};
            const index = state.results.findIndex((result) => result.key === key);
            if (index !== -1) {
                state.results.splice(index, 1);
            }
        },
        updateResults: (state, action: PayloadAction<AdminListPayload>) => {
            const index = state.results.findIndex(result => result.key === action.payload.result.key);
            if (index !== -1) {
                state.results[index] = action.payload.result;
            }
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(getInviteLinkExcel.pending, (state) => {
                state.error = null;
                state.loaded = false;
            })
            .addCase(getInviteLinkExcel.fulfilled, (state, action) => {
                state.error = null;
                state.loaded = true;
                const formattedDate = format(new Date(), 'ddMMyyyy');
                const fileName = `invite-links-${formattedDate}.xlsx`;
                downloadBlob(action.payload, fileName);
            })
            .addCase(getInviteLinkExcel.rejected, (state, action) => {
                state.loaded = true;
                if (typeof action.payload === 'string') {
                    state.error = action.payload as string;
                } else if (typeof action.error?.message === 'string') {
                    state.error = action.error.message;
                }
            })
            .addCase(getFormExcel.pending, (state) => {
                state.error = null;
                state.loaded = false;
            })
            .addCase(getFormExcel.fulfilled, (state, action) => {
                state.error = null;
                state.loaded = true;
                const formattedDate = format(new Date(), 'ddMMyyyy');
                const fileName = `form-data-${formattedDate}.xlsx`;
                downloadBlob(action.payload, fileName);
            })
            .addCase(getFormExcel.rejected, (state, action) => {
                state.loaded = true;
                if (typeof action.payload === 'string') {
                    state.error = action.payload as string;
                } else if (typeof action.error?.message === 'string') {
                    state.error = action.error.message;
                }
            })
            .addCase(getListItems.pending, (state, action) => {
                // Clear results if listType changes
                const incomingListType = action.meta.arg.listType;
                if (state.listType !== incomingListType) {
                    state.results = [];
                    state.count = 0;
                    state.next = null;
                    state.previous = null;
                    state.listType = incomingListType;
                }
                state.loaded = false;
                state.error = null;
            })
            .addCase(getListItems.fulfilled, (state, action) => {
                state.loaded = true;
                state.error = null;
                state.count = action.payload.count;
                state.next = action.payload.next;
                state.previous = action.payload.previous;
                state.results = action.payload.results;
            })
            .addCase(getListItems.rejected, (state, action) => {
                state.loaded = true;
                if (typeof action.payload === 'string') {
                    state.error = action.payload as string;
                } else if (typeof action.error?.message === 'string') {
                    state.error = action.error.message;
                }
            })
    }
});

export const {
    deleteResult,
    clearError,
    updateResults
} = slice.actions;
export default slice.reducer;
